<template>
  <div class="integration">
    <p>
      Agrega funcionalidades que complementen tu experiencia en {{ ProductName }} con nuestras
      integraciones.
      <a :href="DOCS_URL" target="_blank" rel="noopener noreferrer">Click aquí</a> para conocer más.
    </p>

    <section class="grid">
      <IntegrationCard
        v-for="integration in integrations"
        :key="integration.id"
        :integration="integration"
        :integrationInfo="integrationInfo"
        @click="handleOpenIntegrationModal"
      />
    </section>
  </div>
  <AIModal
    v-if="aiModalOpen"
    v-model:open="aiModalOpen"
    :billing-type="billingType"
    :is-edit="hasIntegration"
    :gpt-token="gptToken"
  />

  <RestrictionChangePlanModal
    title="Realiza un upgrade y accede a todo el poder de Beex."
    v-model:open="openRestriction"
    :planRequest="PlanType.AllInOne"
  >
    <template #description>
      Tu plan {{ PlanTypeText[plan as PlanType] }} no incluye esta funcionalidad. <br/>
      Solicita un upgrade y potencia tu ambiente de trabajo. 
      ¡Que tu viaje no se detenga!
    </template>
  </RestrictionChangePlanModal>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch, reactive } from "vue";
import { useStore } from "vuex";

import { Integration, IntegrationType, StatusHubpspot } from "@/@types/integrations";
import { UserData } from "@/@types/global";

import IntegrationCard from "@/app/integrations/components/cards/IntegrationCard.vue";
import AIModal from "@/app/integrations/components/modals/AIModal.vue";
import { integrationManager } from "@/app/integrations/services/IntegrationManager";
import { ProductName, CompanyName } from "@/app/shared/utils/constants";
import httpManager from "@/app/shared/utils/httpManager";
import { hubspotService } from "@/app/channels/services/hubspot.services";
import { PlanType, PlanTypeText } from "@/app/common/types";
import RestrictionChangePlanModal from "@/app/common/components/modals/RestrictionChangePlanModal.vue";

const DOCS_URL = `${import.meta.env.VITE_WEB_DOC}/conexiones/integraciones-1/`;

const integrations: Integration[] = reactive([
  {
    id: IntegrationType.OpenAI,
    title: `${CompanyName} AI`,
    type: "Inteligencia artificial",
    description:
      "Impulsa la eficiencia en la gestión de tus tickets mediante inteligencia artificial. Crea conclusiones de conversaciones para un manejo más efectivo.",
    is_connected: false,
  },
  {
    id: IntegrationType.Hubspot,
    title: `Hubspot`,
    type: "CRM",
    description:
      "Gestiona y sincroniza los datos de tus contactos de BeexCC y HubSpot.",
    is_connected: false,
  },
]);

const store = useStore();
const integrationInfo = ref("");
const gptToken = ref("");
const aiModalOpen = ref(false);

const user = computed(() => store.getters["getDataUser"] as UserData);
const openRestriction = ref(false);
const PlanSpecial = 5;

const plan = computed(() => {
  return user.value?.change_custom_plan?.plan;
});

const billingType = computed(() => {
  return user.value?.customer?.billing_type;
});

const hubspotStatus = computed(() => {
  console.log('integrationInfo.value?.data_hubspot?.state: ', integrationInfo.value?.data_hubspot?.state);
  return integrationInfo.value?.data_hubspot?.state || StatusHubpspot.Disconnected;
});

const hasIntegration = computed(() => {
  if (integrationInfo.value?.enable_summary) return true;
  return false;
});

const fetchIntegration = async () => {
  const response = await integrationManager.getCustomerSetting();
  integrationInfo.value = response;
  gptToken.value = response.gpt_token;

  integrations.forEach((integration) => {
    if (integration.id === IntegrationType.OpenAI) {
      integration.is_connected = hasIntegration.value;
    } else if (integration.id === IntegrationType.Hubspot) {
      console.log("hubspotStatus.value: ", hubspotStatus.value)
      console.log("StatusHubpspot.Integrated: ", StatusHubpspot.Integrated)
      integration.is_connected = hubspotStatus.value === StatusHubpspot.Integrated;
    }
  });
};

onMounted(() => {
  fetchIntegration();
});

const handleLoginHubSpot = async () => {
  try {
    const response = await hubspotService.loginHubspot();
    console.log(response);
    if (response.url_redirect) {
      window.location.href = response.url_redirect;
    }
  } catch (error) {
    console.error("Error al intentar iniciar sesión en HubSpot:", error);
  }
}

const handleOpenIntegrationModal = (integrationId: IntegrationType) => {
  switch (integrationId) {
    case IntegrationType.OpenAI:
      if (![PlanType.AllInOne, PlanSpecial].includes(plan.value))
        openRestriction.value = true;
      else
        aiModalOpen.value = true;
      break;
    case IntegrationType.Hubspot:
      handleLoginHubSpot();
      break;
  }
};

watch(aiModalOpen, (newValue) => {
  fetchIntegration();
});
</script>

<style scoped lang="sass">
.integration
  padding: 10px
  a
    padding: 0
.grid
  display: grid
  margin: 0 25px
  gap: 12px
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr))

p
  color: $gray-8
  margin-bottom: 26px
</style>
